import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, AlertTriangle, RefreshCw, Inbox, ExternalLink, X, MessageSquare, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { brand } from "../../context/CompanyContext";

/**
 * AdminEmails — Resend setup health + contact form inbox.
 * - Shows sender, fallback, admin notification recipient, target domain
 * - Lists Resend domain records if the API key allows (status + SPF/DKIM)
 * - Button to send a real test email to the admin
 * - Lists contact-form submissions with read/unread state
 */
export default function AdminEmails() {
  const [health, setHealth] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [openContact, setOpenContact] = useState(null);

  async function loadAll() {
    setLoading(true);
    try {
      const [h, c] = await Promise.all([
        api.get("/admin/resend/health"),
        api.get("/admin/contact-requests"),
      ]);
      setHealth(h.data);
      setContacts(c.data.items || []);
      setUnread(c.data.unread || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }
  useEffect(() => { loadAll(); }, []);

  async function sendTest() {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/resend/test", {});
      if (data.ok) toast.success(`Enviado para ${data.to}`);
      else toast.error(data.detail || "Falha");
    } catch (e) { toast.error(formatApiError(e)); }
    setTesting(false);
  }

  async function markRead(id) {
    try {
      await api.post(`/admin/contact-requests/${id}/read`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  if (loading) return <div className="text-sm text-[#a0b2af]">A carregar…</div>;

  return (
    <div className="space-y-10" data-testid="admin-emails-page">
      <header>
        <div className="eyebrow text-[#d4af37] flex items-center gap-2">
          <Mail size={14} /> Admin · Emails
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Sistema de emails</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
          Configuração Resend, estado do domínio, envio de teste e caixa de
          pedidos recebidos pelo formulário público.
        </p>
      </header>

      {/* HEALTH */}
      <section className="lux-card p-6 space-y-4" data-testid="resend-health">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-display text-xl">Estado do Resend</div>
          <div className="flex gap-2">
            <button onClick={loadAll}
                    className="px-3 py-2 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] inline-flex items-center gap-2"
                    data-testid="health-refresh">
              <RefreshCw size={12} /> Recarregar
            </button>
            <button onClick={sendTest} disabled={testing}
                    className="btn-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="health-send-test">
              {testing ? "A enviar…" : (<><Send size={12} /> Enviar email de teste</>)}
            </button>
          </div>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Field k="Remetente" v={health?.sender} />
          <Field k="Remetente fallback" v={health?.fallback} />
          <Field k="Domínio" v={health?.target_domain} />
          <Field k="Notificações internas" v={health?.admin_notification_email} />
          <Field k="Estado" v={health?.status}
                 tone={health?.status === "verified" ? "good" : health?.domain_registered === false ? "bad" : "info"} />
          <Field k="Região" v={health?.region} />
        </dl>

        {health?.dns_records?.length > 0 && (
          <div className="space-y-2">
            <div className="eyebrow text-[10px] text-[#a0b2af]">Registos DNS</div>
            <div className="grid gap-2">
              {health.dns_records.map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-4 p-3 border border-[#1a4640] text-xs"
                     data-testid={`dns-record-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {r.status === "verified"
                      ? <CheckCircle2 size={14} className="text-[#10b981] shrink-0" />
                      : <AlertTriangle size={14} className="text-[#f59e0b] shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-[#fdfbf7]">{r.type} · <span className="font-mono">{r.name}</span></div>
                      <div className="text-[#a0b2af] font-mono truncate">{r.value_preview}</div>
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-[#d4af37] shrink-0">
                    {r.status || "—"}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 text-[11px] text-[#a0b2af]">
              <span>SPF: {health.spf_ok ? "✓" : "—"}</span>
              <span>DKIM: {health.dkim_ok ? "✓" : "—"}</span>
            </div>
          </div>
        )}

        {health?.notice && (
          <div className="text-xs text-[#a0b2af] border border-[#f59e0b]/30 bg-[#f59e0b]/5 p-3">
            {health.notice}
            {" "}<a href="https://resend.com/domains" target="_blank" rel="noreferrer"
                   className="text-[#d4af37] hover:underline inline-flex items-center gap-1">
              abrir resend.com/domains <ExternalLink size={10} />
            </a>
          </div>
        )}
      </section>

      {/* CONTACT INBOX */}
      <section className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="font-display text-xl inline-flex items-center gap-2">
            <Inbox size={16} className="text-[#d4af37]" /> Pedidos de contacto
          </div>
          {unread > 0 && (
            <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-[#d4af37] text-[#010c0a]"
                  data-testid="contacts-unread">{unread} por ler</span>
          )}
        </div>
        {contacts.length === 0 ? (
          <div className="lux-card p-8 text-center text-sm text-[#a0b2af]"
               data-testid="contacts-empty">
            Ainda não há pedidos. Assim que alguém enviar o formulário do site, aparece aqui.
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((c) => (
              <article key={c.id} className={`lux-card p-4 cursor-pointer transition-colors hover:border-[#d4af37] ${!c.read ? "border-[#d4af37]/40" : ""}`}
                       onClick={() => { setOpenContact(c); if (!c.read) markRead(c.id); }}
                       data-testid={`contact-${c.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#fdfbf7]">
                      <strong>{c.name}</strong>{" "}
                      <span className="text-[#d4af37] ml-2 text-xs">{c.email}</span>
                      {c.phone && <span className="text-[#a0b2af] ml-2 text-xs font-mono">· {c.phone}</span>}
                    </div>
                    <p className="text-sm text-[#a0b2af] mt-1 line-clamp-2 whitespace-pre-wrap">{c.message}</p>
                    <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] mt-2 flex items-center gap-3 flex-wrap">
                      <span>{new Date(c.created_at).toLocaleString("pt-PT")}</span>
                      {c.replies?.length > 0 && (
                        <span className="text-[#10b981]" data-testid={`contact-replied-${c.id}`}>
                          ✓ {c.replies.length} resposta(s) enviada(s)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-[#a0b2af] shrink-0 inline-flex items-center gap-1 text-xs">
                    <MessageSquare size={12} /> Ver / Responder
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {openContact && (
        <ContactDetailModal
          contact={openContact}
          onClose={() => setOpenContact(null)}
          onReplied={(updatedContact) => {
            setOpenContact(updatedContact);
            loadAll();
          }}
        />
      )}
    </div>
  );
}

function ContactDetailModal({ contact, onClose, onReplied }) {
  const [subject, setSubject] = useState(`Re: o seu pedido — ${brand()}`);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function send(e) {
    e.preventDefault();
    if (subject.trim().length < 2) {
      toast.error("Indique um assunto.");
      return;
    }
    if (message.trim().length < 5) {
      toast.error("Escreva uma resposta com pelo menos 5 caracteres.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/contact-requests/${contact.id}/reply`, {
        subject: subject.trim(),
        message: message.trim(),
      });
      toast.success("Resposta enviada por email.");
      const updated = {
        ...contact,
        replies: [...(contact.replies || []), data.reply],
        read: true,
      };
      setMessage("");
      onReplied?.(updated);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none overflow-y-auto">
        <div className="lux-card bg-[#041f1b] border border-[#1a4640] w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto my-auto"
             data-testid="contact-detail-modal">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a4640] sticky top-0 bg-[#041f1b] z-10">
            <div className="min-w-0 flex-1">
              <div className="eyebrow text-[#d4af37] flex items-center gap-2">
                <Inbox size={14} /> Pedido de contacto
              </div>
              <div className="text-sm text-[#fdfbf7] mt-1 truncate">
                {contact.name} · <span className="text-[#a0b2af]">{contact.email}</span>
              </div>
            </div>
            <button type="button" onClick={onClose}
                    className="p-2 hover:text-[#d4af37] text-[#a0b2af]"
                    data-testid="contact-detail-close">
              <X size={16} />
            </button>
          </div>

          <div className="p-5 space-y-5">
            {/* Sender info */}
            <section className="grid sm:grid-cols-2 gap-3 text-sm">
              <Field k="Nome" v={contact.name} />
              <Field k="Email" v={<a href={`mailto:${contact.email}`} className="text-[#d4af37] hover:underline">{contact.email}</a>} />
              <Field k="Telefone" v={contact.phone || "—"} />
              <Field k="Recebido" v={new Date(contact.created_at).toLocaleString("pt-PT")} />
            </section>

            {/* Original message */}
            <section>
              <div className="eyebrow mb-2 text-[#a0b2af]">Mensagem original</div>
              <div className="p-4 border border-[#1a4640] bg-[#062c27] rounded-sm text-sm text-[#fdfbf7] whitespace-pre-wrap"
                   data-testid="contact-original-message">
                {contact.message}
              </div>
            </section>

            {/* Reply history */}
            {contact.replies?.length > 0 && (
              <section data-testid="contact-replies-history">
                <div className="eyebrow mb-2 text-[#a0b2af]">Respostas enviadas</div>
                <div className="space-y-2">
                  {contact.replies.map((r) => (
                    <div key={r.id} className="p-4 border-l-2 border-[#10b981]/60 bg-[#10b981]/5 rounded-sm">
                      <div className="text-[10px] uppercase tracking-widest text-[#10b981] mb-1.5">
                        {new Date(r.sent_at).toLocaleString("pt-PT")} · {r.sent_by_name || "Admin"}
                      </div>
                      <div className="text-sm text-[#fdfbf7] mb-1.5"><strong>Assunto:</strong> {r.subject}</div>
                      <div className="text-sm text-[#cde3df] whitespace-pre-wrap">{r.message}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Reply form */}
            <section>
              <div className="eyebrow mb-2 text-[#d4af37]">
                {contact.replies?.length > 0 ? "Enviar nova resposta" : "Responder ao cliente"}
              </div>
              <form onSubmit={send} className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Assunto</label>
                  <input type="text" value={subject} required maxLength={200}
                         onChange={(e) => setSubject(e.target.value)}
                         className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none"
                         data-testid="contact-reply-subject" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Mensagem</label>
                  <textarea value={message} required minLength={5} maxLength={4000} rows={6}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Olá, em resposta ao seu pedido…"
                            className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none resize-none"
                            data-testid="contact-reply-message" />
                  <div className="text-[10px] text-[#a0b2af] text-right">{message.length}/4000</div>
                </div>
                <div className="flex justify-end">
                  <button type="submit" disabled={busy}
                          className="btn-gold px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                          data-testid="contact-reply-submit">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                    {busy ? "A enviar…" : "Enviar resposta"}
                  </button>
                </div>
              </form>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ k, v, tone }) {
  const toneMap = {
    good: "text-[#10b981]",
    bad: "text-[#fca5a5]",
    info: "text-[#d4af37]",
  };
  return (
    <div className="flex flex-col gap-1 p-3 border border-[#1a4640]">
      <dt className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{k}</dt>
      <dd className={`text-sm font-mono ${tone ? toneMap[tone] : "text-[#fdfbf7]"} break-all`}>
        {v || "—"}
      </dd>
    </div>
  );
}
